/**
 * The browse ladder both S3 inspectors need: the tenant's S3 connections, the
 * buckets on the chosen one, and the objects under the current prefix.
 *
 * One hook rather than the effects repeated in each inspector — the Blob
 * equivalents predate it and inline them, which is why the same "load the
 * containers" effect appears three times in `PipelineCanvas.tsx`.
 */

import { useEffect, useState } from "react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { listConnections } from "@/shared/api/integration";
import { s3ListBuckets, s3ListFiles } from "@/shared/api/integration-browse";
import { toastApiError } from "@/shared/ui/toast";

export interface S3BrowseState {
  connections: { value: string; label: string }[];
  buckets: string[];
  /** Object keys under `prefix` — full keys, as S3 reports them. */
  objects: string[];
  loading: boolean;
}

export function useS3Browse(
  connectionId: string,
  bucket: string,
  prefix: string | null,
): S3BrowseState {
  const getToken = useAccessToken();
  const [connections, setConnections] = useState<{ value: string; label: string }[]>([]);
  const [buckets, setBuckets] = useState<string[]>([]);
  const [objects, setObjects] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const items = (await listConnections(token, "s3")).items;
        if (!cancelled)
          setConnections(items.map((c) => ({ value: c.connection_id, label: c.display_name })));
      } catch (err) {
        toastApiError("Could not load connections", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  useEffect(() => {
    if (!connectionId) {
      setBuckets([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const items = (await s3ListBuckets(token, connectionId)).items;
        if (!cancelled) setBuckets(items.map((b) => b.name));
      } catch (err) {
        toastApiError("Could not load buckets", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId]);

  useEffect(() => {
    if (!connectionId || !bucket) {
      setObjects([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const token = await getToken();
        const items = (await s3ListFiles(token, connectionId, bucket, prefix)).items;
        if (!cancelled) setObjects(items.map((o) => o.name));
      } catch (err) {
        toastApiError("Could not browse bucket", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId, bucket, prefix]);

  return { connections, buckets, objects, loading };
}

/** Immediate subfolder names under `prefix`, derived from a flat key listing.
 *  S3 has no directories — a folder is only a shared prefix. */
export function subfoldersOf(keys: string[], prefix: string): string[] {
  const subs = new Set<string>();
  for (const key of keys) {
    const rest = key.startsWith(prefix) ? key.slice(prefix.length) : key;
    const slash = rest.indexOf("/");
    if (slash > 0) subs.add(rest.slice(0, slash));
  }
  return [...subs].sort();
}
